<script setup lang="ts">
import type { ICellRendererParams } from "ag-grid-community";
import { computed } from "vue";
import {
  getUrgencyColors,
  type UrgencyCounts,
} from "../features/results/resultHelpers.ts";

const props = defineProps<{
  params?: ICellRendererParams<unknown, unknown>;
  counts?: UrgencyCounts;
  label?: string;
}>();

const INDICATOR_WIDTH_PX = 100;
const MIN_SEGMENT_WIDTH_PX = 6;

const emptyCounts: UrgencyCounts = {
  ok: 0,
  moderate: 0,
  critical: 0,
  na: 0,
  total: 0,
};

function toSafeCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function normalizeUrgencyCounts(value: unknown): UrgencyCounts {
  if (!value || typeof value !== "object") {
    return emptyCounts;
  }

  const counts = value as Partial<Record<keyof UrgencyCounts, unknown>>;
  return {
    ok: toSafeCount(counts.ok),
    moderate: toSafeCount(counts.moderate),
    critical: toSafeCount(counts.critical),
    na: toSafeCount(counts.na),
    total: toSafeCount(counts.total),
    ...(typeof counts.rateOfSaleDays === "number"
      ? { rateOfSaleDays: counts.rateOfSaleDays }
      : {}),
  };
}

function getCountsSource(): unknown {
  const data = props.params?.data as { urgencyCounts?: unknown } | undefined;
  const urgencyCounts = data?.urgencyCounts;
  return urgencyCounts ?? emptyCounts;
}

const urgencyCounts = computed<UrgencyCounts>(() =>
  normalizeUrgencyCounts(getCountsSource())
);
const displayLabel = computed(() => {
  if (typeof props.label === "string") {
    return props.label;
  }

  return typeof props.params?.value === "string" ? props.params.value : "";
});

function tooltipText(
  label: string,
  count: number,
  rateOfSaleDays?: number
): string {
  if (label === "N/A") {
    const days = rateOfSaleDays ?? 30;
    return `${String(count)} SKU${count === 1 ? "" : "s"}: N/A (no ${String(days)}-day Rate Of Sale)`;
  }
  return `${String(count)} SKU${count === 1 ? "" : "s"}: ${label}`;
}

const segments = computed(() => {
  const counts = urgencyCounts.value;
  const total =
    counts.total > 0
      ? counts.total
      : counts.ok + counts.moderate + counts.critical + counts.na;

  const baseSegments = [
    {
      key: "critical",
      label: "Critical",
      count: counts.critical,
      cssClass: "urgency-critical",
    },
    {
      key: "moderate",
      label: "Moderate",
      count: counts.moderate,
      cssClass: "urgency-moderate",
    },
    {
      key: "ok",
      label: "OK",
      count: counts.ok,
      cssClass: "urgency-ok",
    },
    {
      key: "na",
      label: "N/A",
      count: counts.na,
      cssClass: "urgency-na",
    },
  ];

  const visibleSegments =
    total > 0
      ? baseSegments.filter((segment) => segment.count > 0)
      : baseSegments;

  const reservedWidth = visibleSegments.length * MIN_SEGMENT_WIDTH_PX;
  const distributableWidth = Math.max(INDICATOR_WIDTH_PX - reservedWidth, 0);

  return visibleSegments.map((segment, index) => {
    const colors = getUrgencyColors(segment.cssClass);
    const proportionalWidth =
      total > 0 && visibleSegments.length > 0
        ? (segment.count / total) * distributableWidth
        : INDICATOR_WIDTH_PX / Math.max(visibleSegments.length, 1);
    const segmentWidth =
      total > 0 ? proportionalWidth + MIN_SEGMENT_WIDTH_PX : proportionalWidth;

    return {
      ...segment,
      width: `${String(Number(segmentWidth.toFixed(2)))}px`,
      minWidth: `${String(MIN_SEGMENT_WIDTH_PX)}px`,
      tooltip: tooltipText(segment.label, segment.count, counts.rateOfSaleDays),
      backgroundColor: colors.background,
      borderColor: colors.color,
      isFirst: index === 0,
      isLast: index === visibleSegments.length - 1,
    };
  });
});
</script>

<template>
  <div class="urgency-indicator-with-label">
    <div
      class="urgency-indicator"
      role="img"
      aria-label="PO urgency distribution"
    >
      <span
        v-for="segment in segments"
        :key="segment.key"
        class="urgency-indicator-segment"
        :class="{
          'urgency-indicator-segment-first': segment.isFirst,
          'urgency-indicator-segment-last': segment.isLast,
        }"
        :data-tooltip="segment.tooltip"
        :aria-label="segment.tooltip"
        tabindex="0"
        :style="{
          width: segment.width,
          minWidth: segment.minWidth,
          backgroundColor: segment.backgroundColor,
          borderColor: segment.borderColor,
          borderRightColor: segment.borderColor,
          borderLeftColor: segment.isFirst
            ? segment.borderColor
            : 'transparent',
        }"
      />
    </div>
    <span v-if="displayLabel" class="urgency-indicator-label">
      {{ displayLabel }}
    </span>
  </div>
</template>
